/**
 * GraphQL 返回值 → 前端视图类型转换
 *
 * 独立成模块而非写在 hooks 里：列表、详情、溯源三处复用，
 * 内联会产生多份各自演化的副本。
 *
 * 后端字段名与前端视图类型不完全同名（如 targetAddress → address），
 * 转换在此收口，组件层不感知后端命名。
 */
import {
    ExemptTargetType,
    SampleJudgeResult,
    SpotSampleRow,
    SpotTaskConclusion,
    SpotTaskRow,
    SpotTaskSource,
    SpotTaskStatus,
} from '../types/ExemptSpotCheckType';
import {SpotSampleTypeGql, SpotTaskTypeGql} from './queries/spotTaskQueries';

/** 后端返回的枚举字符串转前端枚举；未知值回落默认，避免后端新增枚举时前端崩溃 */
const toTargetType = (value?: string): ExemptTargetType =>
    value === ExemptTargetType.FENCE ? ExemptTargetType.FENCE : ExemptTargetType.ADDRESS;

const toStatus = (value?: string): SpotTaskStatus =>
    value === SpotTaskStatus.DONE ? SpotTaskStatus.DONE : SpotTaskStatus.PENDING;

const toSource = (value?: string): SpotTaskSource => {
    if (value && Object.values(SpotTaskSource).includes(value as SpotTaskSource)) {
        return value as SpotTaskSource;
    }
    return SpotTaskSource.MANUAL;
};

const toConclusion = (value?: string): SpotTaskConclusion | undefined => {
    if (value && Object.values(SpotTaskConclusion).includes(value as SpotTaskConclusion)) {
        return value as SpotTaskConclusion;
    }
    return undefined;
};

const toJudgeResult = (value?: string): SampleJudgeResult => {
    if (value && Object.values(SampleJudgeResult).includes(value as SampleJudgeResult)) {
        return value as SampleJudgeResult;
    }
    return SampleJudgeResult.PENDING;
};

export const toSpotTaskRow = (dto: SpotTaskTypeGql): SpotTaskRow => ({
    id: dto.id,
    taskNo: dto.taskNo ?? '',
    targetType: toTargetType(dto.targetType),
    targetId: dto.targetId ?? 0,
    targetNo: dto.targetNo ?? '',
    // 后端字段名 targetAddress / targetZipcode，前端视图沿用更短的 address / zipcode
    address: dto.targetAddress ?? '',
    zipcode: dto.targetZipcode,
    addressCount: dto.targetAddressCount,
    periodNo: dto.periodNo ?? '',
    source: toSource(dto.source),
    sourceDetail: dto.sourceDetail,
    status: toStatus(dto.status),
    planSampleCount: dto.planSampleCount,
    sampleCount: dto.sampleCount,
    failCount: dto.failCount,
    exemptCount: dto.exemptCount,
    assigneeId: dto.assigneeId,
    assigneeName: dto.assigneeName,
    doneAt: dto.doneAt,
    conclusion: toConclusion(dto.conclusion),
    conclusionRemark: dto.conclusionRemark,
});

export const toSpotSampleRow = (dto: SpotSampleTypeGql): SpotSampleRow => ({
    id: dto.id,
    taskId: dto.taskId ?? 0,
    orderId: dto.orderId ?? 0,
    waybillNo: dto.waybillNo,
    judgeResult: toJudgeResult(dto.judgeResult),
    podImageUrls: dto.podImageUrls ?? [],
    driverName: dto.driverName,
    deliveredAt: dto.deliveredAt,
    judgeRemark: dto.judgeRemark,
    judgedBy: dto.judgedBy,
    judgedByName: dto.judgedByName,
    judgedAt: dto.judgedAt,
});
